/**
 * MafiaVoting — Vote targets with count badges, abstain, real-time display
 *
 * Requirements: 9.1–9.4, 9.9, 21.1, 21.2
 */

import React from 'react';
import { MafiaPlayer } from '../types';
import './MafiaVoting.css';

export interface MafiaVotingProps {
  alivePlayers: MafiaPlayer[];
  currentVotes: Map<string, string | null>;
  playerId: string;
  onVote: (targetId: string | null) => void;
  myCurrentVote: string | null;
  disabled: boolean;
}

export const MafiaVoting: React.FC<MafiaVotingProps> = ({
  alivePlayers,
  currentVotes,
  playerId,
  onVote,
  myCurrentVote,
  disabled,
}) => {
  // Tally votes per target
  const voteTally = new Map<string, number>();
  const voterMap = new Map<string, string[]>();
  for (const [voterId, targetId] of Array.from(currentVotes.entries())) {
    if (targetId !== null) {
      voteTally.set(targetId, (voteTally.get(targetId) || 0) + 1);
      const voters = voterMap.get(targetId) || [];
      voters.push(voterId);
      voterMap.set(targetId, voters);
    }
  }

  // Targets: all alive players except self
  const targets = alivePlayers.filter((p) => p.id !== playerId);

  const handleVote = (targetId: string | null) => {
    if (disabled) return;
    // Toggle: if already voted for this target, abstain; otherwise vote
    if (myCurrentVote === targetId) {
      onVote(null);
    } else {
      onVote(targetId);
    }
  };

  return (
    <div className="mafia-voting" role="region" aria-label="Day Voting">
      <h3 className="mafia-voting__title">🗳️ Vote to Eliminate</h3>

      <ul className="mafia-voting__list" role="radiogroup" aria-label="Vote targets">
        {targets.map((player) => {
          const voteCount = voteTally.get(player.id) || 0;
          const isSelected = myCurrentVote === player.id;

          return (
            <li key={player.id}>
              <button
                className={`mafia-voting__target ${isSelected ? 'mafia-voting__target--selected' : ''} ${disabled ? 'mafia-voting__target--disabled' : ''}`}
                onClick={() => handleVote(player.id)}
                disabled={disabled}
                role="radio"
                aria-checked={isSelected}
                aria-label={`Vote for ${player.name} (${voteCount} votes)`}
              >
                <span className="mafia-voting__target-name">{player.name}</span>
                <span
                  className={`mafia-voting__target-badge ${voteCount > 0 ? 'mafia-voting__target-badge--has-votes' : ''}`}
                >
                  {voteCount}
                </span>
              </button>
            </li>
          );
        })}

        <li>
          <button
            className={`mafia-voting__abstain ${myCurrentVote === null && currentVotes.has(playerId) ? 'mafia-voting__abstain--selected' : ''}`}
            onClick={() => handleVote(null)}
            disabled={disabled}
            aria-label="Abstain from voting"
          >
            🤷 Abstain
          </button>
        </li>
      </ul>
    </div>
  );
};

export default MafiaVoting;
